import React, { useState, useEffect } from 'react';
import { amplifyApi } from '../../services/amplifyApi';
import {
  Loader2,
  RefreshCw,
  AlertCircle,
  CheckCircle,
  Save,
  Zap,
  Package,
  Sparkles,
  Crown,
  Star,
  Gift
} from 'lucide-react';

const TIER_ICONS = {
  free: Gift,
  pro: Star,
  enterprise: Crown
};

const TIER_COLORS = {
  free: { bg: 'bg-slate-100', border: 'border-slate-300', text: 'text-slate-700', icon: 'text-slate-500' },
  pro: { bg: 'bg-blue-50', border: 'border-blue-300', text: 'text-blue-700', icon: 'text-blue-500' },
  enterprise: { bg: 'bg-purple-50', border: 'border-purple-300', text: 'text-purple-700', icon: 'text-purple-500' }
};

function LimitsTab() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);
  const [tiers, setTiers] = useState([]);
  const [editingTier, setEditingTier] = useState(null);
  const [editValues, setEditValues] = useState({});
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    loadTiers();
  }, []);

  const loadTiers = async () => {
    setLoading(true);
    setError(null);

    const response = await amplifyApi.getAdminTierLimits();
    if (response.success) {
      // Sort tiers: free, pro, enterprise
      const tierOrder = ['free', 'pro', 'enterprise'];
      const sortedTiers = (response.tiers || []).sort(
        (a, b) => tierOrder.indexOf(a.tier) - tierOrder.indexOf(b.tier)
      );
      setTiers(sortedTiers);
    } else {
      setError(response.error || 'Failed to load tier limits');
    }

    setLoading(false);
  };

  const handleEdit = (tier) => {
    setEditingTier(tier.tier);
    setEditValues({
      products: { ...tier.products },
      ai: { ...tier.ai }
    });
  };

  const handleCancel = () => {
    setEditingTier(null);
    setEditValues({});
  };

  const handleSave = async (tierName) => {
    setSaving(tierName);
    setError(null);
    setSuccess(null);

    const response = await amplifyApi.updateAdminTierLimits(tierName, editValues);
    if (response.success) {
      setSuccess(`${tierName.charAt(0).toUpperCase() + tierName.slice(1)} tier limits updated`);
      setEditingTier(null);
      setEditValues({});
      loadTiers();
    } else {
      setError(response.error || 'Failed to update tier limits');
    }

    setSaving(null);
  };

  const handleChange = (category, field, value) => {
    setEditValues(prev => ({
      ...prev,
      [category]: {
        ...prev[category],
        [field]: parseInt(value) || 0
      }
    }));
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
        <span className="ml-2 text-slate-600">Loading tier limits...</span>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-slate-900">Tier Limits</h2>
          <p className="text-sm text-slate-500 mt-1">
            Configure product creation and AI usage limits for each subscription tier
          </p>
        </div>
        <button
          onClick={loadTiers}
          className="flex items-center gap-2 px-3 py-2 text-sm text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition-colors"
        >
          <RefreshCw className="w-4 h-4" />
          Refresh
        </button>
      </div>

      {/* Alerts */}
      {error && (
        <div className="flex items-center gap-2 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          {error}
          <button onClick={() => setError(null)} className="ml-auto text-red-500 hover:text-red-700">
            &times;
          </button>
        </div>
      )}
      {success && (
        <div className="flex items-center gap-2 p-4 bg-green-50 border border-green-200 rounded-lg text-green-700">
          <CheckCircle className="w-5 h-5 flex-shrink-0" />
          {success}
          <button onClick={() => setSuccess(null)} className="ml-auto text-green-500 hover:text-green-700">
            &times;
          </button>
        </div>
      )}

      {/* Tier Cards */}
      <div className="grid md:grid-cols-3 gap-6">
        {tiers.map((tier) => {
          const isEditing = editingTier === tier.tier;
          const values = isEditing ? editValues : tier;
          const colors = TIER_COLORS[tier.tier] || TIER_COLORS.free;
          const TierIcon = TIER_ICONS[tier.tier] || Gift;

          return (
            <div
              key={tier.tier}
              className={`rounded-xl border-2 ${colors.border} ${colors.bg} p-5`}
            >
              {/* Tier Header */}
              <div className="flex items-center gap-3 mb-4">
                <div className={`w-10 h-10 rounded-full ${colors.bg} border ${colors.border} flex items-center justify-center`}>
                  <TierIcon className={`w-5 h-5 ${colors.icon}`} />
                </div>
                <div>
                  <h3 className={`text-lg font-bold ${colors.text}`}>
                    {tier.tier.charAt(0).toUpperCase() + tier.tier.slice(1)}
                  </h3>
                  <p className="text-xs text-slate-500">{tier.description}</p>
                </div>
              </div>

              {/* Product Limits */}
              <div className="mb-4">
                <div className="flex items-center gap-2 mb-2">
                  <Package className="w-4 h-4 text-slate-400" />
                  <span className="text-sm font-medium text-slate-700">Product Limits</span>
                </div>
                <div className="space-y-2 ml-6">
                  <LimitField
                    label="Per Session"
                    value={values.products?.sessionMax}
                    isEditing={isEditing}
                    onChange={(v) => handleChange('products', 'sessionMax', v)}
                  />
                  <LimitField
                    label="Per Day"
                    value={values.products?.dailyMax}
                    isEditing={isEditing}
                    onChange={(v) => handleChange('products', 'dailyMax', v)}
                  />
                  <LimitField
                    label="Per Month"
                    value={values.products?.monthlyMax}
                    isEditing={isEditing}
                    onChange={(v) => handleChange('products', 'monthlyMax', v)}
                  />
                </div>
              </div>

              {/* AI Limits */}
              <div className="mb-4">
                <div className="flex items-center gap-2 mb-2">
                  <Sparkles className="w-4 h-4 text-slate-400" />
                  <span className="text-sm font-medium text-slate-700">AI Limits</span>
                </div>
                <div className="space-y-2 ml-6">
                  <LimitField
                    label="Generations/Hour"
                    value={values.ai?.generationPerHour}
                    isEditing={isEditing}
                    onChange={(v) => handleChange('ai', 'generationPerHour', v)}
                  />
                  <LimitField
                    label="Generations/Day"
                    value={values.ai?.generationPerDay}
                    isEditing={isEditing}
                    onChange={(v) => handleChange('ai', 'generationPerDay', v)}
                  />
                  <LimitField
                    label="BG Removal/Hour"
                    value={values.ai?.bgRemovalPerHour}
                    isEditing={isEditing}
                    onChange={(v) => handleChange('ai', 'bgRemovalPerHour', v)}
                  />
                  <LimitField
                    label="BG Removal/Day"
                    value={values.ai?.bgRemovalPerDay}
                    isEditing={isEditing}
                    onChange={(v) => handleChange('ai', 'bgRemovalPerDay', v)}
                  />
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-2 mt-4 pt-4 border-t border-slate-200">
                {isEditing ? (
                  <>
                    <button
                      onClick={() => handleSave(tier.tier)}
                      disabled={saving === tier.tier}
                      className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm disabled:opacity-50"
                    >
                      {saving === tier.tier ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Save className="w-4 h-4" />
                      )}
                      Save
                    </button>
                    <button
                      onClick={handleCancel}
                      className="px-4 py-2 text-slate-600 hover:bg-slate-200 rounded-lg transition-colors text-sm"
                    >
                      Cancel
                    </button>
                  </>
                ) : (
                  <button
                    onClick={() => handleEdit(tier)}
                    className="flex-1 px-4 py-2 text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors text-sm"
                  >
                    Edit Limits
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Empty State */}
      {tiers.length === 0 && (
        <div className="text-center py-12">
          <Zap className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-slate-600 mb-2">No Tier Limits Configured</h3>
          <p className="text-slate-500 mb-4">
            Run the seed script to create default tier configurations.
          </p>
          <code className="bg-slate-100 px-4 py-2 rounded-lg text-sm text-slate-700">
            node scripts/seed-tier-limits.js dev
          </code>
        </div>
      )}

      {/* Info Box */}
      <div className="bg-slate-100 rounded-lg p-4 text-sm text-slate-600">
        <strong>Limit Types:</strong>
        <ul className="mt-2 space-y-1 list-disc list-inside">
          <li><strong>Product Limits</strong>: Control how many products users can create per session, day, and month</li>
          <li><strong>AI Limits</strong>: Control AI image generation and background removal usage</li>
          <li>Limits are checked independently - both product AND AI limits must be available</li>
          <li>Per-user overrides can be set in the Users tab for individual exceptions</li>
        </ul>
      </div>
    </div>
  );
}

// Reusable limit field component
function LimitField({ label, value, isEditing, onChange }) {
  return (
    <div className="flex items-center justify-between text-sm">
      <span className="text-slate-600">{label}</span>
      {isEditing ? (
        <input
          type="number"
          min="0"
          value={value || 0}
          onChange={(e) => onChange(e.target.value)}
          className="w-20 px-2 py-1 text-right border border-slate-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
        />
      ) : (
        <span className="font-medium text-slate-900">{value?.toLocaleString() || 0}</span>
      )}
    </div>
  );
}

export default LimitsTab;
